package it.unibo.tuprolog.solve.libs.oop.primitives

import it.unibo.tuprolog.core.Atom
import it.unibo.tuprolog.core.Struct
import it.unibo.tuprolog.core.Term
import it.unibo.tuprolog.solve.ExecutionContext
import it.unibo.tuprolog.solve.libs.oop.Ref
import it.unibo.tuprolog.solve.primitive.Solve
import it.unibo.tuprolog.solve.primitive.TernaryRelation

/**
 * `assign(+Ref, +PropertyName, +Value)`: assigns `Value` to the mutable property named
 * `PropertyName` (an [it.unibo.tuprolog.core.Atom]) on `Ref` -- an
 * [it.unibo.tuprolog.solve.libs.oop.ObjectRef], [it.unibo.tuprolog.solve.libs.oop.TypeRef], or
 * `$Alias` expression -- resolving `Value` itself as a `$Alias` expression first, if it looks like
 * one. Succeeds deterministically if the assignment was performed.
 *
 * This is what the fluent `:=`/2 syntax lowers to for property writes (see
 * [it.unibo.tuprolog.solve.libs.oop.rules.ColonEquals.Assignment]): `Obj.name := joe` compiles down
 * to `assign(Obj, name, joe)`.
 *
 * @throws it.unibo.tuprolog.solve.libs.oop.exceptions.PropertyAssignmentException if `Ref` has no
 * mutable property named `PropertyName` accepting a value compatible with `Value`.
 */
object Assign : TernaryRelation.Predicative<ExecutionContext>("assign") {
    override val help: String =
        """
        `assign(+Ref, +Property, +Value)`
        
        Writes `Value` into the public mutable property named `Property` of the object (or, for a type reference, the static/companion member) referenced by `Ref`. `Ref` must be an object or type reference (not a `${'$'}Alias` term); `Value` is converted to the property's type (it may use `as` casts or be a `${'$'}Alias` reference). If several properties share the name, the one whose type best fits `Value` is chosen. Succeeds once. Raises a type error if `Ref` is not a reference or `Property` is not an atom, an existence error if no suitable mutable property exists, and a representation error if `Value` cannot be converted. This is what `Obj.prop := Value` (see `:=/2`) lowers to.

        **Examples**

        ```prolog
        ?- new_object('java.awt.Point', [1, 2], P), assign(P, x, 5), X := P.x.
        X = 5.

        ?- new_object('java.awt.Point', [1, 2], P), assign(P, nope, 5).
        throws error(existence_error(_, _), _).

        ?- assign(foo, x, 5).
        throws error(type_error(_, foo), _).
        ```
        """.trimIndent()

    override fun Solve.Request<ExecutionContext>.compute(
        first: Term,
        second: Term,
        third: Term,
    ): Boolean {
        ensuringArgumentIsAtom(0)
        ensuringArgumentIsAtom(1)

        return catchingOopExceptions {
            val ref =
                when (first) {
                    is Ref -> first
                    else -> findRefFromAlias(first as Atom)
                }

            val value =
                if (match(third, DEALIASING_TEMPLATE)) {
                    findRefFromAlias(third as Struct)
                } else {
                    third
                }

            ref.assign(termToObjectConverter, (second as Atom).value, value)
        }
    }
}
